import { globalHistory } from '@reach/router';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';

import { ReactComponent as GlobeIcon } from '/src/images/icons/globe.svg';
import { ReactComponent as SearchIcon } from '/src/images/icons/search.svg';

import { extractQueryParams } from '../../helpers/floor-plan/floor-plan-helpers';
import { isBrowser } from '../../helpers/is-browser';
import { useOnClickOutside } from '../../hooks';
import { IContentfulImage } from '../../interfaces/contentful-image.interface';
import { ILibraryMeetingPlace } from '../../queries/fragments/libraryMeetingPlace';
import { ILibraryMenuGroup } from '../../queries/fragments/libraryMenuGroup';
import { ILibrarySocialMediaLinks } from '../../queries/fragments/librarySocialMediaLinks';
import { IMeetingPlacePageSearchResults } from '../../queries/fragments/meetingPlacePageSearchResults';
import { PageContext } from '../../templates/page-context.interface';
import { ITextAndImageCardBlock } from '../blocks/text-and-image-card-block/text-and-image-card-block';
import { FoldOutMenu } from '../fold-out-menu';
import { LanguageSelectorWidget } from '../language-selector/language-selector-widget';
import { SearchWidget } from '../search/search-widget';

interface TabBarProps {
  menuGroups: Array<ILibraryMenuGroup>;
  menuCards: ITextAndImageCardBlock[];
  logo: IContentfulImage;
  socialMediaLinks: ILibrarySocialMediaLinks;
  meetingPlace: ILibraryMeetingPlace;
  node_locale: string;
  searchResults?: IMeetingPlacePageSearchResults;
  pageContext: PageContext;
}

export const TabBar = ({
  menuGroups,
  menuCards,
  logo,
  socialMediaLinks,
  meetingPlace,
  node_locale,
  searchResults,
  pageContext,
}: TabBarProps) => {
  const ref = useRef() as MutableRefObject<HTMLDivElement>;
  const refSearch = useRef() as MutableRefObject<HTMLDivElement>;
  const refLanguageSelector = useRef() as MutableRefObject<HTMLDivElement>;
  const [showMenu, setShowMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [routeExists, setRouteExists] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  useOnClickOutside(ref, () => setShowMenu(false));
  useOnClickOutside(refSearch, () => setShowSearch(false));
  useOnClickOutside(
    refLanguageSelector,
    () => setShowLanguageSelector(false),
    false,
    ['language-selector-trigger-button'],
  );
  useEffect(() => {
    showMenu
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [showMenu]);

  useEffect(() => {
    showSearch
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [showSearch]);

  const [scrollDown, setScrollDown] = useState(false);

  // Hides mobile menu on scroll down. Shows on scroll up.
  // https://stackoverflow.com/a/64371350/790522

  let lastScrollPos: number = (isBrowser && window.scrollY) || 0;
  const setScroll = () => {
    if ((isBrowser && window.scrollY < 0) || (isBrowser && lastScrollPos < 0))
      return; // This if statement fixes a bug on iOS Safari. Without it the hamburger menu will be hidden when user scrolls up so fast that the page "bounces".
    setScrollDown(isBrowser && window.scrollY > lastScrollPos);
    lastScrollPos = (isBrowser && window.scrollY) || 0;
  };

  useEffect(() => {
    document.addEventListener('scroll', setScroll);
    return () => {
      document.removeEventListener('scroll', setScroll);
    };
  });

  useEffect(() => {
    const params = extractQueryParams();
    if (params.kioskId != '') {
      setRouteExists(true);
    } else {
      setRouteExists(false);
    }
  }, [isBrowser && window.location.search]);

  const toggleMenu = () => setShowMenu(!showMenu);
  const toggleSearch = () => setShowSearch(!showSearch);
  const toggleLanguageSelector = () =>
    setShowLanguageSelector(!showLanguageSelector);

  // Following effect hides the menu when user clicks something in it. Before the menu wasnt closed if you clicked a link/card pointing to the same page you are on.
  useEffect(() => {
    if (showMenu === false) return;
    return globalHistory.listen(({ action }) => {
      if (action === 'PUSH') setShowMenu(false);
    });
  }, [showMenu]);

  return (
    <nav
      aria-label="Main menu"
      className={`fixed bottom-5 left-1/2 z-50 flex min-w-max -translate-x-1/2 transform items-center justify-around rounded-xl bg-brand-white shadow-default transition-all max-lg:flex-row-reverse lg:absolute lg:bottom-auto lg:left-auto lg:right-0 lg:top-0 lg:w-27 lg:min-w-0 lg:transform-none lg:rounded-none lg:bg-transparent lg:shadow-transparent ${
        scrollDown ? 'translate-y-24 lg:translate-y-0' : ''
      }`}
      data-elastic-exclude
    >
      {!routeExists && (
        <>
          {' '}
          <button
            onClick={toggleMenu}
            type="button"
            className="box-content grid h-15 w-15 place-items-center content-center focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 lg:fixed lg:top-6 lg:rounded-full lg:bg-brand-yellow-1 lg:transition-all lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
            aria-expanded={showMenu}
            aria-controls="main-menu"
            aria-label="Open main menu"
          >
            <span className="m-px h-1 w-5 rounded-sm bg-brand-grey-3 lg:bg-brand-grey-4"></span>
            <span className="m-px h-1 w-5 rounded-sm bg-brand-grey-3 lg:bg-brand-grey-4"></span>
            <span className="m-px h-1 w-5 rounded-sm bg-brand-grey-3 lg:bg-brand-grey-4"></span>
          </button>
          <FoldOutMenu
            el={ref}
            menuClass={
              showMenu
                ? ''
                : 'translate-y-[calc(100%+10rem)] lg:translate-y-0 lg:hidden'
            }
            toggleMenu={toggleMenu}
            logo={logo}
            menuGroups={menuGroups}
            menuCards={menuCards}
            socialMediaLinks={socialMediaLinks}
            meetingPlace={meetingPlace}
            pageContext={pageContext}
          />
        </>
      )}

      {searchResults && !routeExists && (
        <button
          type="button"
          onClick={toggleSearch}
          className={`grid h-15 w-15 place-items-center text-icon-dark outline-0 transition-all lg:fixed lg:top-28 lg:h-10 lg:w-10 lg:rounded-full lg:bg-brand-white lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200
        ${showSearch ? 'hidden' : ''}`}
          aria-expanded={showSearch}
          aria-controls="search-widget"
          aria-label="Open search"
        >
          <SearchIcon />
        </button>
      )}
      {showSearch && searchResults && (
        <SearchWidget
          el={refSearch}
          logo={logo}
          toggleSearch={toggleSearch}
          searchClass={
            showSearch ? '' : 'translate-y-full lg:-translate-y-[7rem]'
          }
          node_locale={node_locale}
          meetingPlace={meetingPlace}
          searchResults={searchResults}
        />
      )}

      {meetingPlace.showLanguageSelector && (
        <>
          <button
            id="language-selector-trigger-button"
            type="button"
            onClick={toggleLanguageSelector}
            className="grid h-15 w-15 place-items-center text-icon-dark outline-0 transition-all max-lg:hidden lg:fixed lg:top-56 lg:h-10 lg:w-10 lg:rounded-full lg:hover:bg-neutral-grey-100 lg:focus-visible:bg-brand-white lg:focus-visible:outline lg:focus-visible:outline-3 lg:focus-visible:outline-offset-3 lg:focus-visible:outline-icon-dark lg:active:bg-neutral-grey-200"
            aria-expanded={showLanguageSelector}
            aria-controls="language-selector"
            aria-label="Select language"
          >
            <GlobeIcon />
          </button>

          <LanguageSelectorWidget
            el={refLanguageSelector}
            hrefLangTags={pageContext.hrefLangTags}
            node_locale={node_locale}
            languageSelectorClass={
              showLanguageSelector
                ? 'fixed right-7 top-68 max-lg:hidden'
                : 'hidden'
            }
            variant="dropdown"
            primaryLanguage={meetingPlace.primaryLanguage}
            secondaryLanguages={meetingPlace.languages ?? []}
          />
        </>
      )}
    </nav>
  );
};
